import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faMapMarkerAlt, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, colorHelpers } from '../../utils/colors';

/**
 * LocationDetails Component
 * - Modal displaying detailed information about a location
 * - Shows all location fields in organized sections
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.location - Location data to display
 * @param {boolean} props.loading - Whether location data is loading
 */
const LocationDetails = ({ isOpen, onClose, location, loading = false }) => {
  const { t } = useTranslation();
  const strings = t('locations', { returnObjects: true });

  // Small presentational row used inside modal. Mirrors Users' InfoRow.
  const InfoRow = ({ icon, label, value, highlight = false }) => {
    const { t } = useTranslation();
    const display = value || t('common.symbols.emDash') || '-';
    return (
      <div
        className="flex items-start justify-between rounded-xl border px-4 py-3"
        style={{
          borderColor: UI_COLORS.BORDERS.LIGHT,
          backgroundColor: highlight ? colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.05) : UI_COLORS.BACKGROUNDS.CARD,
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg"
            style={{
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.3),
              color: UI_COLORS.TEXT.PRIMARY,
            }}
          >
            {icon && <FontAwesomeIcon icon={icon} className="text-base" />}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>{label}</p>
            <p className="text-base font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{display}</p>
          </div>
        </div>
      </div>
    );
  };


  if (!isOpen) return null;

  const locName = location ? (location.delivery_location || location.name || strings.table?.copy?.unnamedLocation) : strings.table?.copy?.unnamedLocation;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <div className="user-view-modal w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        <div className="flex items-center justify-between border-b px-6 py-5" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {strings.details.title}
            </p>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {locName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}></div>
              <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.messages.loading}</p>
            </div>
          ) : !location ? (
            <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-4xl opacity-60" style={{ color: UI_COLORS.TEXT.MUTED }} />
              <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{strings.messages.noData}</h3>
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <InfoRow icon={faBuilding} label={strings.details.fields.name} value={location.delivery_location || location.name} />
                <InfoRow icon={faMapMarkerAlt} label={strings.details.fields.city} value={location.city} />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <InfoRow icon={faMapMarkerAlt} label={strings.details.fields.state} value={location.state} />
                <InfoRow icon={faMapMarkerAlt} label={strings.details.fields.country} value={location.country} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationDetails;